import i18n from 'i18next';
import {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';
import {LiveRoomReportDTO} from '../incase-domain/analytics';
import {ConnectionStatusNotificationType} from '../incase-domain/connectionStatus';
import {IRoomRunFile} from '../incase-domain/proptypes/IRoomRunFile';
import {RoomStatus} from '../incase-domain/rooms/room.player';
import {updateLiveRoomReportAction} from '../store/actions/analytics.actions';
import {endPresentationAction} from '../store/actions/chalkBoardElements.actions';
import {updateConnectionStatusAction} from '../store/actions/connectionStatus.actions';
import {closeModalAction} from '../store/actions/modal.actions';
import {updateCurrentSlide} from '../store/actions/presentation.actions';
import {updateRoomReducerAction, updateRoomRunFileAction, updateRoomUserCount} from '../store/actions/roomRunFile.actions';
import {roomSelector} from '../store/selectors/roomRunFile.selectors';
import {runFileIdSelector} from '../store/selectors/runfile.selectors';
import {createSocketConnection, socket} from '../util/socket';
import {setRoomInfoModalAction} from '../store/actions/roomProtection.actions';
import {loggedInUserSelector} from '../store/selectors/auth.selector';

/**
 * join and handle socket messages if in room
 */
export function useRoomSocket(): void {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const room = useSelector(roomSelector);
    const runFileId = useSelector(runFileIdSelector);
    const loggedInUser = useSelector(loggedInUserSelector);

    useEffect(() => {
        const connectionStatusSuccess = {
            type: ConnectionStatusNotificationType.SUCCESS,
            message: 'connectionStatus.connected' as const,
            hidden: false,
        };

        const connectionStatusSuccessHidden = {
            type: ConnectionStatusNotificationType.SUCCESS,
            message: 'connectionStatus.connected' as const,
            hidden: true,
        };

        const connectionStatusError = {
            type: ConnectionStatusNotificationType.ERROR,
            message: 'connectionStatus.notConnected' as const,
            hidden: false,
        };

        if (room.roomId) {
            const {roomId, socketToken} = room;
            const query: Record<string, string> = {roomId, ...(runFileId ? {runfileId: runFileId} : {})};
            createSocketConnection(query, socketToken);

            if (socket) {
                socket.connect();

                socket.on('connect_error', (err) => {
                    if (err.message === 'PRESENTER_ALREADY_ACTIVE') {
                        socket?.disconnect();
                        const [title, description] = loggedInUser
                            ? (['lobby.modals.presenterActiveOtherDevice', 'lobby.modals.presenterActiveOtherDeviceDesc'] as const)
                            : (['lobby.modals.presenterAlreadyActive', 'lobby.modals.presenterAlreadyActiveDesc'] as const);
                        dispatch(setRoomInfoModalAction(true, title, description));
                    }
                });

                socket.on('connect', () => {
                    if (socket?.recovered) {
                        dispatch(updateConnectionStatusAction(connectionStatusSuccess));
                        setTimeout(() => {
                            dispatch(updateConnectionStatusAction(connectionStatusSuccessHidden));
                        }, 5000);
                    } else {
                        dispatch(updateConnectionStatusAction(connectionStatusSuccessHidden));
                    }

                    if (room.isPresenter && (room.roomStatus === RoomStatus.IDLE || !room.roomStatus)) {
                        socket && socket.emit('lobbyReady');
                    }
                });

                socket.on('userCount', (count) => {
                    dispatch(updateRoomUserCount(count));
                });

                socket.on('closeRoom', () => {
                    dispatch(endPresentationAction(navigate));
                    toast.info(i18n.t('message.info.roomClosing'), {toastId: 'roomClosing'});
                });

                socket.on('startRoom', () => {
                    dispatch(updateRoomReducerAction({...room, roomStatus: RoomStatus.PLAYING}));
                });

                socket.on('roomStatusUpdate', (data: {status: RoomStatus}) => {
                    dispatch(updateRoomReducerAction({...room, roomStatus: data.status}));
                });

                socket.on('startRejected', () => {
                    toast.warning(i18n.t('roomSchedule.startNotAllowed'), {toastId: 'startNotAllowed'});
                });

                socket.on('roomRunfile', (data: IRoomRunFile) => {
                    dispatch(updateRoomRunFileAction(data));
                    dispatch(updateCurrentSlide(data.ownerCurrentSlideIndex));
                    dispatch(closeModalAction());
                });

                socket.on('liveRoomReport', (data: LiveRoomReportDTO) => {
                    dispatch(updateLiveRoomReportAction(data));
                });

                socket.on('disconnect', (reason, details) => {
                    // eslint-disable-next-line no-console
                    console.log(reason);
                    // eslint-disable-next-line no-console
                    console.log(details);

                    if (reason === 'ping timeout') {
                        dispatch(updateConnectionStatusAction(connectionStatusError));
                    }
                });
            }
        }

        return () => {
            if (socket) {
                socket.disconnect();
                socket.removeAllListeners();
            }
        };
    }, [dispatch, room, navigate, runFileId, loggedInUser]);
}
